<script setup>
  import AppDrawerLinks from '@/components/AppDrawerLinks.vue';
  import WorkspaceLogo from '@/components/WorkspaceLogo.vue';
  import AppDashboardLayout from '@/layouts/AppDashboardLayout.vue';
  import router from '@/router/index.js';
  import { useAccountStore } from '@/stores/account.store.js';
  import { useModalStore } from '@/stores/modal.store.js';
  import { useSettingsStore } from '@/stores/settings.store.js';
  import { useWorkspaceStore } from '@/stores/workspace.store.js';
  import { useQueryClient } from '@tanstack/vue-query';
  import truncate from 'truncate';
  import { computed, onBeforeMount } from 'vue';

  const queryClient = useQueryClient();
  const modal = useModalStore();
  const accounts = computed(() => useAccountStore().accounts);
  const selectedAccount = ref(useSettingsStore().activeAccount);
  const activeWorkspace = computed(() => useSettingsStore().activeWorkspace);
  const selectedWorkspace = computed({
    get() {
      return useSettingsStore().activeWorkspace;
    },
    set(value) {
      useSettingsStore().setActiveWorkspace(value);
    },
  });

  const profileLinks = [
    {
      text: 'Home',
      icon: 'mdi-home-outline',
      to: '/',
      disabled: false,
      action: async () => {
        await useWorkspaceStore().setActiveWorkspace(null);
        router.push('/');
      },
    },
    {
      text: 'Profile',
      icon: 'mdi-account-settings-outline',
      to: '/settings/profile',
    },
    {
      text: 'Password & Security',
      icon: 'mdi-lock-outline',
      to: '/settings/password',
    },
    // {
    //   text: 'Notifications',
    //   icon: 'mdi-bell-badge-outline',
    //   to: '/settings/account-notifications'
    // }
  ];

  const workspaceLinks = [
    {
      text: 'Workspace Details',
      icon: 'mdi-domain',
    },
  ];

  const loadSettingsInitialData = async () => {
    await useSettingsStore().getSettingsPermissions();
    await useSettingsStore().getWorkspaces();
  };

  onBeforeMount(async () => {
    if (useSettingsStore().activeAccount) {
      await loadSettingsInitialData();
    }
  });

  const switchAccountHandle = async (account) => {
    if (account) {
      useSettingsStore().setActiveAccount(account);
      selectedWorkspace.value = null;
      await loadSettingsInitialData();
    }
  };

  const switchWorkspaceHandle = async (workspace) => {
    useSettingsStore().setActiveWorkspace(workspace);
    if (workspace) {
      await useSettingsStore().getSettingsPermissions();
      await router.push({ name: 'Workspace Details' });
      await queryClient.invalidateQueries({ queryKey: ['loadWorkspaceTeamMembers'] });
    }
  };
</script>

<template>
  <AppDashboardLayout page-title="Settings">
    <template #drawer>
      <div v-if="profileLinks.length > 0" class="tw-w-full tw-pt-3">
        <AppDrawerLinks :links="profileLinks" />
      </div>
      <div class="tw-mt-3 tw-p-2">
        <v-divider></v-divider>
        <div class="tw-my-2 tw-ml-2 tw-text-[0.90rem] tw-font-bold">Accounts</div>
        <v-select
          v-model="selectedAccount"
          @update:modelValue="switchAccountHandle(selectedAccount)"
          :items="accounts"
          item-title="name"
          return-object
          density="compact"
          variant="solo"
          hide-details
          placeholder="Select Account"
        ></v-select>
        <template v-if="selectedAccount">
          <ul class="tw-mt-5 tw-flex tw-flex-col tw-space-y-1">
            <li class="tw-flex tw-w-full tw-items-center tw-rounded-lg tw-pb-2 tw-pl-0 tw-pt-0">
              <RouterLink :to="`/settings/account-details`" exact class="tw-group tw-w-full tw-rounded-md tw-p-1 hover:tw-bg-[#eaecf4]">
                <div class="tw-flex tw-w-full tw-items-center tw-justify-between">
                  <div class="tw-flex tw-items-center tw-space-x-2">
                    <!--                    <WorkspaceLogo :workspace="activeWorkspace" size="2rem" :use-thumbnail="true" />-->
                    <v-img contain v-if="selectedAccount?.image" class="tw-w-7 tw-rounded-full" :src="selectedAccount?.image" :alt="`${selectedAccount?.name} account logo`"></v-img>
                    <div>
                      <div class="tw-link-text tw-text-[0.980rem] tw-font-semibold tw-text-primary-black">
                        {{ truncate(selectedAccount.name, 13) }}
                      </div>
                      <div class="tw-text-[0.7rem]">Account Details</div>
                    </div>
                  </div>
                </div>
              </RouterLink>
            </li>
          </ul>
        </template>
      </div>

      <div class="tw-mt-3 tw-p-2">
        <v-divider></v-divider>
        <div class="tw-my-2 tw-ml-2 tw-text-[0.90rem] tw-font-bold">Workspaces</div>
        <v-combobox
          v-model="selectedWorkspace"
          :items="useSettingsStore().workspaces"
          item-title="name"
          return-object
          density="compact"
          variant="solo"
          hide-details
          :loading="useSettingsStore().isLoadingWorkspaces"
          :disabled="useSettingsStore().isLoadingWorkspaces || selectedAccount === null"
          clearable
          placeholder="Select a workspace"
          class="tw-cursor-pointer"
          @update:modelValue="switchWorkspaceHandle"
        >
          <template v-slot:item="{ props, item }">
            <v-list-item :class="item.raw.archived ? 'tw-line-through' : ''" v-bind="props" :subtitle="item.raw.title" density="compact" style="font-size: 0.875rem !important">
              <template v-slot:prepend>
                <WorkspaceLogo :workspace="item.raw" :use-thumbnail="true" style="margin-right: 5px; width: 1.7rem; height: 1.7rem; border-radius: 9999px" />
              </template>
            </v-list-item>
          </template>
        </v-combobox>
        <div class="tw-mb-1 tw-py-4">
          <v-btn prepend-icon="mdi-plus" color="#069aad" class="tw-text-small tw-h-9 tw-normal-case" variant="tonal" @click="modal.showCreateNewWorkspaceModal(true)" block size="small">
            Create Workspace
          </v-btn>
        </div>

        <ul class="tw-flex tw-flex-col tw-space-y-1">
          <li class="tw-flex tw-w-full tw-items-center tw-rounded-lg tw-pb-2 tw-pl-0 tw-pt-0" v-for="(link, index) in workspaceLinks" :key="index">
            <template v-if="activeWorkspace && !useSettingsStore().isLoadingWorkspaces">
              <RouterLink :to="`/settings/workspace-details`" exact class="tw-group tw-w-full tw-rounded-md tw-p-1 hover:tw-bg-[#eaecf4]">
                <div class="tw-flex tw-w-full tw-items-center tw-justify-between">
                  <div class="tw-flex tw-items-center tw-space-x-2">
                    <WorkspaceLogo :workspace="activeWorkspace" size="2rem" :use-thumbnail="true" />
                    <div>
                      <div class="tw-link-text tw-text-[0.980rem] tw-font-semibold tw-text-primary-black">
                        {{ truncate(activeWorkspace.name, 13) }}
                      </div>
                      <div class="tw-text-[0.7rem]">Workspace Details</div>
                    </div>
                  </div>
                </div>
              </RouterLink>
            </template>
            <template v-else>
              <div class="tw-group tw-w-full tw-cursor-pointer tw-rounded-md tw-bg-[#e6e6e6f7] tw-p-1">
                <div class="tw-flex tw-h-8 tw-w-full tw-flex-row tw-items-center tw-justify-between">
                  <div class="tw-flex tw-items-center tw-p-1">
                    <v-icon class="tw-mr-4 tw-h-5 tw-w-5" color="#808080">
                      {{ link.icon }}
                    </v-icon>

                    <span class="link-text tw-text-[0.850rem] tw-text-[#808080]">
                      {{ link.text }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </li>
        </ul>
      </div>
    </template>
    <template #main>
      <div class="tw-mt-4">
        <div class="tw-h-full tw-w-full tw-overflow-hidden tw-px-0 tw-pl-0 lg:tw-px-11 lg:tw-pl-4">
          <div class="lg:tw-px-4">
            <router-view v-slot="{ Component }">
              <component :is="Component" />
            </router-view>
          </div>
        </div>
      </div>
    </template>
  </AppDashboardLayout>
</template>

<style scoped>
  .router-link-exact-active {
    @apply tw-bg-[#d7dbeb];
  }
</style>
